import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';

import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';

export type KPISkeletonVariant = 'value' | 'valueSm' | 'tag' | 'row' | 'label';

const VARIANT_CONTAINER: Record<KPISkeletonVariant, string> = {
  value: 'h-[18px] 4xl:h-[24px] block leading-[unset!important]',
  valueSm: 'h-6 block leading-[unset!important]',
  tag: 'h-[24px] block leading-[unset!important]',
  row: 'h-[30px] block leading-[unset!important]',
  label: 'h-[12px] block leading-[unset!important]',
};

interface KPISkeletonLoaderProps {
  width: number;
  variant?: KPISkeletonVariant;
  className?: string;
}

export function KPISkeletonLoader({ width, variant = 'value', className }: KPISkeletonLoaderProps) {
  return (
    <Skeleton
      containerClassName={VARIANT_CONTAINER[variant]}
      className={clsx('leading-[unset!important] rounded-[8px]', VARIANT_CONTAINER[variant], className)}
      baseColor="var(--color-gray-200)"
      highlightColor="#FFFF"
      width={width}
    />
  );
}

interface KPIMetricSkeletonProps {
  labelWidth?: number;
  valueWidth?: number;
}

export function KPIMetricSkeleton({ labelWidth = 70, valueWidth = 50 }: KPIMetricSkeletonProps) {
  return (
    <div className="flex flex-col gap-[9px] min-w-0 justify-end h-full ">
      <KPISkeletonLoader width={labelWidth} variant="label" />
      <KPISkeletonLoader width={valueWidth} variant="valueSm" />
    </div>
  );
}

interface KPIMetricGroupProps {
  label: string;
  value: string;
}

export function KPIMetricGroup({ label, value }: KPIMetricGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="caption" color="text-gray-700">
        {label}
      </Typography>
      <Typography
        fontWeight={600}
        variant={'H3-resp'}
        color="text-primary-900"
        className="font-family-outfit whitespace-nowrap"
      >
        {value}
      </Typography>
    </div>
  );
}

interface KPIHeaderProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  className?: string;
}

export function KPIHeader({
  icon: Icon,
  title,
  subtitle,
  className,
}: KPIHeaderProps) {
  return (
    <div
      className={clsx(
        'flex items-center gap-2 min-w-0 whitespace-nowrap text-ellipsis overflow-hidden',
        className
      )}
    >
      <div className="shrink-0 size-9">
        <Icon className="size-9! rounded-[12px]" />
      </div>
      <div className="flex flex-col min-w-0">
        <Typography
          variant="p3"
          fontWeight={500}
          color="text-primary-900"
          className="whitespace-nowrap text-ellipsis overflow-hidden truncate"
          title={title}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text-gray-700 font-family-inter"
          className={clsx(
            'whitespace-nowrap text-ellipsis overflow-hidden truncate w-full',
          )}
          title={subtitle}
        >
          {subtitle}
        </Typography>
      </div>
    </div>
  );
}

interface KPIMetricColumnProps {
  label: string;
  value: string;
  volumeLabel?: string;
  loading?: boolean;
  valueVariant?: 'H3-resp' | 'H3-med' | 'H1-med';
}

export function KPIMetricColumn({
  label,
  value,
  volumeLabel,
  loading = false,
  valueVariant = 'H3-resp',
}: KPIMetricColumnProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 min-w-0 justify-end">
        <KPISkeletonLoader width={90} variant="label" />
        <div className="flex items-center gap-2">
          <KPISkeletonLoader width={50} variant="value" />
          {volumeLabel && <KPISkeletonLoader width={58} variant="tag" />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-0 justify-end">
      <Typography variant="caption" color="text-gray-700 font-family-inter">
        {label}
      </Typography>
      <div className="flex items-center gap-2">
        <Typography
          variant={valueVariant}
          fontWeight={600}
          color="text-primary-900"
          className="whitespace-nowrap"
        >
          {value}
        </Typography>
        {volumeLabel && <Tag variant="kpi" size="kpi" text={volumeLabel} showFullText />}
      </div>
    </div>
  );
}

interface StorageChangeTagProps {
  changePercent: string;
  className?: string;
}

export function StorageChangeTag({ changePercent, className }: StorageChangeTagProps) {
  const isReduction = changePercent.startsWith('-');
  const isIncrease = changePercent.startsWith('+');

  return (
    <Tag
      size="status"
      variant={isReduction ? 'aws-mint-green' : isIncrease ? 'orange' : 'aws-gray-light'}
      text={changePercent}
      className={clsx(className, isIncrease && 'text-orange-700')}
      showFullText
    />
  );
}
